import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { logger } from "../../lib/logger";
import { UserPlus, Mail, X, Send, Users, Upload } from "lucide-react";
import BulkImportModal from "../BulkImportModal";

export default function DomainMatchInvites() {
  const { t } = useI18n();
  const [matches, setMatches] = useState([]);
  const [pending, setPending] = useState([]);
  const [manualEmail, setManualEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    try {
      const [{ data: m }, { data: p }] = await Promise.all([
        api.get("/org/me/domain-matches"),
        api.get("/org/me/user-invites"),
      ]);
      setMatches(m || []);
      setPending(p || []);
    } catch (err) {
      logger.warn("DomainMatchInvites: load failed", err);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (email) => {
    setBusy(true);
    try {
      await api.post("/org/me/user-invites", { email });
      toast.success(t("uinv.sent"));
      setManualEmail("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    try {
      await api.delete(`/org/me/user-invites/${id}`);
      toast.success(t("uinv.cancelled"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="domain-match-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 label-tech">
          <Users size={14} /> {t("uinv.title")}
        </div>
        <button onClick={() => setShowImport(true)} className="btn-ghost text-xs" data-testid="org-invite-import-btn">
          <Upload size={12} /> {t("bulk.import")}
        </button>
      </div>
      <p className="label-tech mb-4 max-w-prose">{t("uinv.help")}</p>

      {matches.length > 0 ? (
        <div className="border border-[var(--ink-200)]">
          {matches.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-[var(--ink-200)] last:border-b-0" data-testid={`match-${u.user_id}`}>
              <div className="min-w-0">
                <div className="text-sm truncate">{u.name || u.email}</div>
                <div className="label-tech truncate">{u.email}</div>
              </div>
              <button onClick={() => invite(u.email)} disabled={busy} className="btn-primary text-xs px-3 py-1.5" data-testid={`invite-match-${u.user_id}`}>
                <UserPlus size={12} /> {t("uinv.inviteBtn")}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="label-tech italic">{t("uinv.noMatches")}</div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); if (manualEmail) invite(manualEmail.toLowerCase()); }} className="mt-4 flex gap-2 items-end" data-testid="manual-invite-form">
        <div className="flex-1">
          <div className="label-tech mb-1 inline-flex items-center gap-1"><Mail size={12} /> {t("uinv.manualEmail")}</div>
          <input type="email" className="input-tech" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="kollega@foretag.se" data-testid="manual-invite-email" />
        </div>
        <button type="submit" disabled={busy || !manualEmail} className="btn-primary" data-testid="manual-invite-btn">
          <Send size={14} /> {t("uinv.send")}
        </button>
      </form>

      {pending.length > 0 && (
        <div className="mt-6">
          <div className="label-tech mb-2">{t("uinv.pendingOutgoing")}</div>
          <div className="border border-[var(--ink-200)]">
            {pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-[var(--ink-200)] last:border-b-0" data-testid={`pending-${p.id}`}>
                <div className="text-sm truncate">{p.invited_email}</div>
                <button onClick={() => cancel(p.id)} className="btn-ghost text-xs px-2" data-testid={`cancel-invite-${p.id}`}>
                  <X size={12} /> {t("uinv.cancel")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showImport && (
        <BulkImportModal
          title={t("bulk.title.orgInvites")}
          description={t("bulk.desc.orgInvites")}
          uploadEndpoint="/org/me/users/bulk-invite"
          templateEndpoint="/org/me/users/bulk-invite/template"
          onClose={() => setShowImport(false)}
          onSuccess={() => load()}
          resultLabels={{ createdKey: "invited", skippedKey: "skipped" }}
        />
      )}
    </div>
  );
}
